import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import app from 'flarum/forum/app';
import { ticketUrl } from '../utils/ticketUrl';
import { attachmentError, attachmentUrl, isImageAttachment, pastedImage, uploadAttachment } from '../utils/attachments';
const T = (key, params) => app.translator.trans('cengizkemec-support.forum.' + key, params);
const PRIORITIES = ['low', 'medium', 'high', 'critical'];

/** Field types rendered as <input type="..."> rather than a control widget. */
const INPUT_TYPES = ['text', 'number', 'email', 'url', 'date'];
export default class CreateTicketModal extends Modal {
  subject = Stream('');
  message = Stream('');
  priority = Stream('medium');
  categoryId = Stream('');
  attachment = Stream('');
  attachmentName = Stream('');
  /** Local object URL used to preview the file before it is submitted. */
  preview = Stream('');
  categories = [];
  /** Extra fields the administrator added to the ticket form (HESK style). */
  formFields = [];
  /** One stream per field, keyed by the field's machine name. */
  fieldStreams = {};
  fieldsError = false;
  loading = false;
  uploading = false;
  oninit(vnode) {
    super.oninit(vnode);
    app.store.find('ticket-categories').then(cats => {
      this.categories = cats.filter(c => c.isActive());
      m.redraw();
    });
    app.store.find('ticket-form-fields', {
      sort: 'position',
      include: 'category'
    }).then(fields => {
      fields.forEach(field => {
        const name = field.name();
        if (name) this.fieldStreams[name] = Stream(field.type() === 'checkbox' ? [] : '');
      });
      this.formFields = fields;
      m.redraw();
    }).catch(() => {
      // A missing definition must never block opening a ticket: the form
      // simply renders with the standard fields.
      this.fieldsError = true;
    });
  }
  className() {
    return 'CreateTicketModal Modal--large';
  }
  title() {
    return T('new_ticket');
  }
  selectedCategory() {
    if (!this.categoryId()) return null;
    return app.store.getById('ticket-categories', this.categoryId());
  }

  /**
   * The custom fields that apply to the category the user has picked: global
   * fields (no category) are always shown, category specific ones only once
   * that category is chosen.
   */
  visibleFields() {
    const categoryId = this.categoryId();
    return this.formFields.filter(field => {
      if (!field.isActive()) return false;
      const category = field.category();
      if (!category) return true;
      return !!categoryId && String(category.id()) === String(categoryId);
    }).sort((a, b) => (a.position() || 0) - (b.position() || 0));
  }
  fieldValue(field) {
    const stream = this.fieldStreams[field.name()];
    return stream ? stream() : '';
  }
  toggleCheckbox(field, option) {
    const name = field.name();
    if (!this.fieldStreams[name]) this.fieldStreams[name] = Stream([]);
    const current = this.fieldStreams[name]() || [];
    const index = current.indexOf(option);
    this.fieldStreams[name](index >= 0 ? current.filter(v => v !== option) : current.concat([option]));
    m.redraw();
  }
  renderField(field) {
    const name = field.name();
    const type = field.type();
    const required = !!field.isRequired();
    const options = (field.options() || []).filter(o => typeof o === 'string' && o !== '');
    const stream = this.fieldStreams[name] || Stream('');
    if (!name) return null;
    let input;
    if (type === 'textarea') {
      input = m("textarea", {
        className: "FormControl",
        rows: 4,
        value: stream(),
        oninput: e => stream(e.target.value)
      });
    } else if (type === 'select') {
      input = m("select", {
        className: "FormControl",
        value: stream(),
        onchange: e => stream(e.target.value)
      }, m("option", {
        value: ""
      }, "\u2014"), options.map(option => m("option", {
        value: option
      }, option)));
    } else if (type === 'radio') {
      input = m("div", {
        className: "TicketChoices",
        role: "radiogroup"
      }, options.map(option => m("label", {
        className: "TicketChoice",
        key: option
      }, m("input", {
        type: "radio",
        name: 'ticket-field-' + name,
        value: option,
        checked: stream() === option,
        onchange: () => stream(option)
      }), m("span", null, option))));
    } else if (type === 'checkbox') {
      const selected = stream() || [];
      input = m("div", {
        className: "TicketChoices"
      }, options.map(option => m("label", {
        className: "TicketChoice",
        key: option
      }, m("input", {
        type: "checkbox",
        checked: selected.indexOf(option) >= 0,
        onchange: () => this.toggleCheckbox(field, option)
      }), m("span", null, option))));
    } else if (INPUT_TYPES.indexOf(type) >= 0) {
      input = m("input", {
        className: "FormControl",
        type: type,
        value: stream(),
        oninput: e => stream(e.target.value)
      });
    } else {
      input = m("input", {
        className: "FormControl",
        type: "text",
        value: stream(),
        oninput: e => stream(e.target.value)
      });
    }
    return m("div", {
      className: 'Form-group TicketFormField' + (required ? ' is-required' : ''),
      key: name
    }, m("label", null, field.label(), required && m("span", {
      className: "TicketFormField-required"
    }, " *")), input);
  }

  /**
   * Answers to the custom fields, ready to be sent with the ticket.
   */
  collectCustomFields() {
    const values = {};
    this.visibleFields().forEach(field => {
      const name = field.name();
      const value = this.fieldValue(field);
      const empty = Array.isArray(value) ? value.length === 0 : value === '' || value === null || value === undefined;
      if (empty) return;
      values[name] = value;
    });
    return values;
  }

  /** @returns the translation key of the first missing field, or null. */
  firstUnfilledField() {
    const missing = this.visibleFields().filter(field => {
      if (!field.isRequired()) return false;
      const value = this.fieldValue(field);
      return Array.isArray(value) ? value.length === 0 : value === '' || value === null || value === undefined;
    });
    return missing.length ? missing[0] : null;
  }
  content() {
    const selected = this.selectedCategory();
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "Form"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, T('subject')), m("input", {
      className: "FormControl",
      value: this.subject(),
      oninput: e => this.subject(e.target.value),
      placeholder: T('subject_placeholder'),
      required: true
    })), m("div", {
      className: "Form-group"
    }, m("label", null, T('category')), m("select", {
      className: "FormControl",
      value: this.categoryId(),
      onchange: e => this.categoryId(e.target.value)
    }, m("option", {
      value: ""
    }, T('category_none')), this.categories.map(cat => m("option", {
      value: String(cat.id())
    }, cat.name()))), selected && selected.description() && m("div", {
      className: "TicketCategoryHint"
    }, selected.description())), m("div", {
      className: "Form-group"
    }, m("label", null, T('priority_label')), m("select", {
      className: "FormControl",
      value: this.priority(),
      onchange: e => this.priority(e.target.value)
    }, PRIORITIES.map(p => m("option", {
      value: p
    }, T('priority.' + p))))), this.visibleFields().map(field => this.renderField(field)), m("div", {
      className: "Form-group"
    }, m("label", null, T('message')), m("textarea", {
      className: "FormControl",
      rows: 6,
      value: this.message(),
      oninput: e => this.message(e.target.value),
      onpaste: this.onPaste.bind(this),
      placeholder: T('message_placeholder'),
      required: true
    }), m("div", {
      className: "AttachmentHint"
    }, T('attachment_hint'))), m("div", {
      className: "Form-group"
    }, m("label", null, T('attachment')), m("div", {
      className: "TicketUploadRow"
    }, m("input", {
      type: "file",
      className: "FormControl",
      accept: "image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,application/zip",
      onchange: this.onFileChange.bind(this),
      disabled: this.uploading
    }), this.uploading && m("span", {
      className: "AttachmentStatus"
    }, T('uploading'))), this.attachmentArea()), m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-paper-plane",
      loading: this.loading,
      disabled: this.loading || this.uploading || !this.subject() || !this.message() || !!this.firstUnfilledField(),
      onclick: this.submit.bind(this)
    }, T('create')))));
  }
  attachmentArea() {
    const filename = this.attachment();
    if (!filename) return null;
    const label = this.attachmentName() || T('attachment');
    return m("div", {
      className: "AttachmentRow"
    }, isImageAttachment(filename) ? m("span", {
      className: "AttachmentPreview"
    }, m("img", {
      className: "AttachmentPreview-image",
      src: this.preview() || attachmentUrl(filename),
      alt: label
    }), m("button", {
      type: "button",
      className: "AttachmentRemove",
      title: T('remove_attachment'),
      onclick: () => this.clearAttachment()
    }, m("i", {
      className: "icon fas fa-times"
    }))) : m("span", {
      className: "AttachmentChip"
    }, m("i", {
      className: "icon fas fa-paperclip"
    }), " ", label, m("button", {
      type: "button",
      className: "AttachmentRemove",
      title: T('remove_attachment'),
      onclick: () => this.clearAttachment()
    }, m("i", {
      className: "icon fas fa-times"
    }))));
  }
  onPaste(e) {
    const file = pastedImage(e);
    if (!file) return;
    e.preventDefault();
    this.attach(file);
  }
  onFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    this.attach(file);
  }
  async attach(file) {
    if (this.uploading) return;
    const problem = attachmentError(file);
    if (problem) {
      app.alerts.show({
        type: 'error'
      }, T('upload_' + problem));
      return;
    }
    this.uploading = true;
    m.redraw();
    try {
      const response = await uploadAttachment(file);
      this.attachment(response.filename);
      this.attachmentName(response.originalName || file.name || '');
      // Preview the local file: the server URL only works once saved.
      if (file.type.indexOf('image/') === 0) {
        this.revokePreview();
        this.preview(URL.createObjectURL(file));
      }
    } catch (err) {
      app.alerts.show({
        type: 'error'
      }, T('upload_failed'));
    } finally {
      this.uploading = false;
      m.redraw();
    }
  }
  revokePreview() {
    if (this.preview()) {
      URL.revokeObjectURL(this.preview());
      this.preview('');
    }
  }
  clearAttachment() {
    this.revokePreview();
    this.attachment('');
    this.attachmentName('');
    m.redraw();
  }
  async submit() {
    const missing = this.firstUnfilledField();
    if (missing) {
      app.alerts.show({
        type: 'error'
      }, missing.label() + ': ' + T('field_required'));
      return;
    }
    this.loading = true;
    m.redraw();
    try {
      const data = {
        subject: this.subject(),
        message: this.message(),
        priority: this.priority()
      };
      const customFields = this.collectCustomFields();
      if (Object.keys(customFields).length) {
        data.customFields = customFields;
      }
      if (this.attachment()) {
        data.attachment = this.attachment();
      }
      if (this.categoryId()) {
        data.relationships = {
          category: app.store.getById('ticket-categories', this.categoryId())
        };
      }
      const ticket = await app.store.createRecord('tickets').save(data);
      this.revokePreview();
      app.modal.close();
      m.route.set(ticketUrl(ticket));
    } catch (e) {
      this.loading = false;
      app.alerts.show({
        type: 'error'
      }, e?.response?.errors?.[0]?.detail || e?.message || T('error_create'));
      m.redraw();
    }
  }
}
flarum.reg.add('cengizkemec-support-tickets', 'forum/components/CreateTicketModal', CreateTicketModal);